import { useMessages } from '@ynap/immutables-provider'
import { useConfig } from '@ynap/immutables-provider'
import useDeliveryTracking, { IDeliveryTrackingEvent, deliveryCategory } from './useDeliveryTracking'
import {
    orderCancelledStatus,
    returnRefusedStatus,
    buildTimeline
} from '../utils/order-status/order-tracking-timelines'
import {
    SHIPPED,
    IN_TRANSIT,
    DELIVERED,
    RETURN_INTRANSIT,
    RETURN_RECEIVED,
    RETURN_ACCEPTED,
    RETURN_REFUNDED,
    RETURN_REFUSED,
    RETURN_PARTIALLY_REFUNDED,
    TRANSFERRED
} from '../utils/order-status/constants'
import {
    IUseOrderTrackingHook,
    IOrderTrackingEvent,
    IOrderTrackingData,
    TimelineOptions
} from '../utils/order-status/interfaces'

export interface IOrderTrackingInformation {
    orderStatus: string,
    orderId: string,
    trackingNumber: string,
    orderInformation: OrderInformation
}

export type OrderInformation = {
    timeDelivered: string | undefined,
    dateShipped: string | undefined,
    shippingCountry: string,
    shippingMethod: Array<string>,
    isReturn: boolean,
    isPartialReturnRefused: boolean,
}

const useOrderTracking = (orderInfo: IOrderTrackingInformation): IUseOrderTrackingHook => {
    const {
        orderStatus,
        orderId,
        trackingNumber,
        orderInformation: {
            timeDelivered,
            dateShipped,
            shippingCountry,
            shippingMethod,
            isReturn,
            isPartialReturnRefused,
        }
    } = orderInfo

    const messages = useMessages()
    const config = useConfig()

    const isPremierOrder: boolean = shippingMethod?.some(method => method?.toLowerCase().includes('premier'))

    const isDeliveryTrackingServiceEnabled: boolean = config?.head?.deliveryTrackingService?.enabled
    let isTrackableOrder: boolean = isDeliveryTrackingServiceEnabled && !isPremierOrder && !!trackingNumber && !!orderId

    if (!isReturn) {
        isTrackableOrder = isTrackableOrder && orderStatus === SHIPPED
    }

    const { courier, estimatedDeliveryDate, deliveryTrackingData, isFetchComplete } = useDeliveryTracking(
        orderId,
        trackingNumber,
        isTrackableOrder
    )

    let orderTrackingStatus = orderStatus
    let inTransitAndExceptionEvents: Array<IOrderTrackingEvent> = []
    let orderDeliveryDate = ''

     const isJITEnabled = orderStatus === TRANSFERRED;

    const options: TimelineOptions = {
        isJITEnabled: isJITEnabled,
        isPremierOrder,
        isReturn,
        isPartialReturnRefused,
        isReturnNotSupportedInCountry: config?.head?.deliveryTrackingService?.returnNotSupportedInCountries?.includes(shippingCountry)
    }

    if (isTrackableOrder) {
        const inTransitData = deliveryTrackingData.filter(
            ({ category }) => category !== deliveryCategory.DELIVERY
        )
        const deliveryData = deliveryTrackingData.filter(
            ({ category }) => category === deliveryCategory.DELIVERY
        )

        if (inTransitData.length > 0 || !!estimatedDeliveryDate) {
            inTransitAndExceptionEvents = mapDeliveryTrackingEvents(inTransitData, options)
            const currentEvent = inTransitAndExceptionEvents?.[inTransitAndExceptionEvents?.length - 1]?.status

            orderTrackingStatus =  currentEvent || setInTransitStatus(isReturn)
        }

        if (deliveryData.length > 0) {
            orderTrackingStatus = setDeliveredStatus(isReturn)
            orderDeliveryDate = deliveryData[0].date || ''
        }
    }

    // necessary in case inTransitData is null but order has one of these status
    if (orderStatus === RETURN_ACCEPTED || orderStatus === RETURN_REFUNDED || orderStatus === RETURN_REFUSED) {
        orderTrackingStatus = orderStatus
        orderDeliveryDate = '' // orderDeliveryDate clean up 
    }

    if (isPartialReturnRefused) {
        orderTrackingStatus = RETURN_PARTIALLY_REFUNDED
        orderDeliveryDate = '' // orderDeliveryDate clean up 
    }

    // verify uf order is already delivered
    if (orderStatus === SHIPPED && isOrderDelivered(timeDelivered, dateShipped)) {
        orderTrackingStatus = DELIVERED
        orderDeliveryDate = orderDeliveryDate || timeDelivered || ''
    }

    const orderTrackingData = buildTimeline(inTransitAndExceptionEvents, messages, options) as Array<IOrderTrackingData>
    const orderPrimaryStatus = getOrderPrimaryStatus(orderTrackingStatus, orderTrackingData, messages)

    return {
        orderTrackingStatus,
        orderTrackingData,
        orderEstimatedDeliveryDate: !isReturn ? estimatedDeliveryDate : '',
        orderDeliveryDate,
        courier: courier,
        isFinalStatus: isTrackableOrder ? isFetchComplete : true,
        orderPrimaryStatus,
    }
}

export default useOrderTracking

const setInTransitStatus = (isReturn: boolean) => {
    return isReturn ? RETURN_INTRANSIT : IN_TRANSIT
}

const setDeliveredStatus = (isReturn: boolean) => {
    return isReturn ? RETURN_RECEIVED : DELIVERED
}

const mapDeliveryTrackingEvents = (deliveryTrackingData: Array<IDeliveryTrackingEvent>, options: TimelineOptions): Array<IOrderTrackingEvent> =>
    deliveryTrackingData.map((deliveryTrackingEvent) => {
        const { orderEvents, returnEvents } = useMessages()
        const { description = '', date = '', externalLocation = '', key = '' } = deliveryTrackingEvent
        const country = externalLocation?.split(',')?.[1] || externalLocation
        const eventsMessages = options.isReturn ? returnEvents : orderEvents

        return {
            status: key,
            description: eventsMessages?.[key]?.({country}) || description, 
            date: date,
            location: externalLocation,
        }
    })

const getOrderPrimaryStatus = (
    orderTrackingStatus: string,
    orderTrackingData: Array<IOrderTrackingData>,
    messages: any
): IOrderTrackingData => {
    const emptyStatus = { status: '', title: '', events: [], dotStatus: 'none' }
    const allTrackingStatus = [...orderTrackingData, ...orderCancelledStatus(messages), ...returnRefusedStatus(messages)] as IOrderTrackingData[]

    const primaryStatus = allTrackingStatus.find(({ status, events = [] }) => {
        return status === orderTrackingStatus || events.find((s) => s.status === orderTrackingStatus)
    })

    return primaryStatus || emptyStatus
}

const isOrderDelivered = (timeDelivered: string | undefined, dateShipped: string | undefined): boolean => {
    if (timeDelivered) return true
    if (dateShipped) {
        const diffInMs = new Date().getTime() - new Date(dateShipped).getTime()
        const diffInDays = diffInMs / (1000 * 60 * 60 * 24)
        return diffInDays > 28 // order delivered after 28 days since dateShipped
    }
    return false
}
